import Storage from 'local-storage-fallback'
import type {Action} from 'redux'

import type {AllActions, AsyncAction} from './actions'


function dropKey<K extends string, M>(data: M, key: K): Omit<M, K> {
  const {[key]: omittedKey, ...remaining} = data
  return remaining
}

type StorageType = typeof Storage | typeof window.sessionStorage


function getJsonFromStorage<T>(storageKey: string, storage: StorageType = Storage): T|null {
  const storedJson = storage.getItem(storageKey)
  if (!storedJson) {
    return null
  }
  return JSON.parse(storedJson) as T
}


export function setJsonToStorage(
  storageKey: string, value: unknown, storage: StorageType = Storage): void {
  storage.setItem(storageKey, JSON.stringify(value))
}


export const appInitialData: AppState = {
  // Whether the app loading time has already been measured.
  hasLoadedApp: false,
  // Cache for full jobGroupInfos.
  jobGroupInfos: {},
  // Cache of labor stats per project.
  laborStats: {},
  // Cache for local stats.
  localStats: {},
}


export const addIfKeyExists = <CacheType, K extends keyof CacheType>(
  cacheField: undefined|CacheType,
  key?: K,
  value?: CacheType[K],
): undefined|CacheType => {
  if (!key || value === undefined) {
    return cacheField
  }
  return {
    ...cacheField,
    [key]: value,
  } as CacheType
}

function app(state: AppState = appInitialData, action: AllActions): AppState {
  switch (action.type) {
    case 'PAGE_IS_LOADED':
      return {
        ...state,
        hasLoadedApp: true,
      }
    case 'GET_LOCAL_STATS':
      if (action.status === 'success') {
        const romeId = action.project?.targetJob?.jobGroup?.romeId || ''
        const departementId = action.project?.city?.departementId || ''
        const localId = `${romeId}:${departementId}`
        return {
          ...state,
          jobGroupInfos: addIfKeyExists(
            state.jobGroupInfos,
            romeId,
            action.response.jobGroupInfo || {},
          ),
          laborStats: addIfKeyExists(
            state.laborStats,
            localId,
            action.response,
          ),
          localStats: addIfKeyExists(
            state.localStats,
            localId,
            action.response.localStats || {},
          ),
        }
      }
      return state
  }
  return state
}

const asyncInitialData = {
  errorMessage: undefined,
  isFetching: {},
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function isAsyncAction(action: Action<any>): action is AsyncAction<any, any> {
  return !!(action as AsyncAction<any, any>).ASYNC_MARKER
}
/* eslint-enable @typescript-eslint/no-explicit-any */

function asyncState(state: AsyncState<AllActions> = asyncInitialData, action: AllActions):
AsyncState<AllActions> {
  if (action.type === 'HIDE_TOASTER_MESSAGE') {
    return {...state, errorMessage: undefined}
  }
  if (action.type === 'DISPLAY_TOAST_MESSAGE') {
    return {...state, errorMessage: action.error}
  }
  if (!isAsyncAction(action)) {
    return state
  }
  if (action.status === 'success') {
    return {
      ...dropKey(state, 'authMethod'),
      errorMessage: undefined,
      isFetching: {...state.isFetching, [action.fetchingKey || action.type]: false},
    }
  }
  const isFetching = {...state.isFetching, [action.fetchingKey || action.type]: true}
  return {
    ...state,
    isFetching,
  }
}


export {app, asyncState, getJsonFromStorage}
